import { useState, useCallback, useEffect, useRef } from "react";
import { 
  Edit, 
  Trash2, 
  Star,
  StarOff,
  ChevronRight,
  ChevronLeft,
  Clock,
  MoreHorizontal,
  Paperclip,
  FileText,
  Download,
  X,
  Loader2,
  Eye,
  ExternalLink,
  Pencil,
  Check
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { RichTextEditor } from "./RichTextEditor";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { TeamKnowledgeBaseEntry, KnowledgeBaseAttachment } from "@/types/team";

interface ConfluencePageViewProps {
  entry: TeamKnowledgeBaseEntry;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onBack: () => void;
  onSave: (data: { title: string; content: string; category?: string; is_pinned?: boolean; attachments?: KnowledgeBaseAttachment[] }, entryId?: string) => Promise<boolean>;
  canEdit: boolean;
  creatorName?: string;
  creatorAvatar?: string;
}

export const ConfluencePageView = ({
  entry,
  onEdit,
  onDelete,
  onTogglePin,
  onBack,
  onSave,
  canEdit,
  creatorName,
  creatorAvatar
}: ConfluencePageViewProps) => {
  const { user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(entry.title);
  const [editContent, setEditContent] = useState(entry.content);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState<Date | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<KnowledgeBaseAttachment | null>(null);
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState<number | null>(null);
  const [editingAttachmentName, setEditingAttachmentName] = useState("");
  
  // Load attachments from entry
  const [attachments, setAttachments] = useState<KnowledgeBaseAttachment[]>(entry.attachments || []);
  
  // Update attachments when entry changes
  useEffect(() => {
    setAttachments(entry.attachments || []);
  }, [entry.attachments]);
  const originalTitleRef = useRef(entry.title);
  const originalContentRef = useRef(entry.content);

  // Helper to check if file is previewable
  const isImageFile = (type: string) => type.startsWith('image/');
  const isPdfFile = (type: string) => type === 'application/pdf' || type.includes('pdf');

  const initials = creatorName
    ? creatorName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const handleStartEdit = () => {
    setEditTitle(entry.title);
    setEditContent(entry.content);
    originalTitleRef.current = entry.title;
    originalContentRef.current = entry.content;
    setLastAutoSaved(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTitle(entry.title);
    setEditContent(entry.content);
    setLastAutoSaved(null);
  };

  // Check if there are unsaved changes
  const hasChanges = editTitle !== originalTitleRef.current || editContent !== originalContentRef.current;

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (!editTitle.trim() || !editContent.trim()) return;
    if (!hasChanges) return;
    if (isSaving || isAutoSaving) return;
    
    setIsAutoSaving(true);
    try {
      const success = await onSave({
        title: editTitle.trim(),
        content: editContent.trim(),
        category: entry.category,
        is_pinned: entry.is_pinned,
        attachments: attachments
      }, entry.id);
      
      if (success) {
        originalTitleRef.current = editTitle.trim();
        originalContentRef.current = editContent.trim();
        setLastAutoSaved(new Date());
      }
    } finally {
      setIsAutoSaving(false);
    }
  }, [editTitle, editContent, entry.category, entry.is_pinned, entry.id, hasChanges, isSaving, isAutoSaving, onSave]);

  // Auto-save every 30 seconds when editing
  useEffect(() => {
    if (!isEditing) return;
    
    const interval = setInterval(() => {
      performAutoSave();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [isEditing, performAutoSave]);

  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editContent.trim()) return;
    
    setIsSaving(true);
    try {
      // Pass entry.id to ensure update instead of create
      const success = await onSave({
        title: editTitle.trim(),
        content: editContent.trim(),
        category: entry.category,
        is_pinned: entry.is_pinned,
        attachments: attachments
      }, entry.id);
      if (success) {
        originalTitleRef.current = editTitle.trim();
        originalContentRef.current = editContent.trim();
        setIsEditing(false);
        toast.success('Änderungen gespeichert');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user) return;

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} ist zu groß (max. 10MB)`);
          continue;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${file.name}`;

        const { error: uploadError } = await supabase.storage
          .from('knowledge-base-images')
          .upload(fileName, file);

        if (uploadError) {
          toast.error(`Fehler beim Hochladen von ${file.name}`);
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('knowledge-base-images')
          .getPublicUrl(fileName);

        const newAttachment: KnowledgeBaseAttachment = {
          name: file.name,
          url: publicUrl,
          type: file.type,
          size: file.size
        };

        setAttachments(prev => [...prev, newAttachment]);
        toast.success(`${file.name} hochgeladen`);
      }
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  }, [user]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
    return '📎';
  };

  // Inline editing mode
  if (isEditing && canEdit) {
    return (
      <div className="flex-1 flex flex-col h-full bg-white dark:bg-background">
        {/* Inline Edit Toolbar */}
        <div className="border-b bg-white dark:bg-card px-3 md:px-6 py-2 md:py-3 flex items-center justify-between shrink-0 gap-2">
          <nav className="flex items-center gap-1 text-xs md:text-sm">
            <button 
              onClick={handleCancelEdit}
              className="text-muted-foreground hover:text-foreground"
            >
              ← <span className="hidden sm:inline">Abbrechen</span>
            </button>
            <ChevronRight className="h-4 w-4 text-muted-foreground hidden sm:block" />
            <span className="text-foreground font-medium hidden sm:block">Bearbeiten</span>
          </nav>

          <div className="flex items-center gap-2">
            {/* Auto-save indicator */}
            {isAutoSaving && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="hidden sm:inline">Auto-Speichern...</span>
              </span>
            )}
            {!isAutoSaving && lastAutoSaved && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Gespeichert {formatDistanceToNow(lastAutoSaved, { addSuffix: true, locale: de })}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelEdit}
              disabled={isSaving || isAutoSaving}
              className="hidden sm:flex"
            >
              Verwerfen
            </Button>
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={isSaving || !editTitle.trim() || !editContent.trim()}
              className="bg-primary hover:bg-primary/90 text-xs md:text-sm"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 md:mr-2 animate-spin" />
                  <span className="hidden sm:inline">Speichern...</span>
                </>
              ) : (
                'Speichern'
              )}
            </Button>
          </div>
        </div>

        {/* Inline Editor */}
        <ScrollArea className="flex-1">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-8">
            {/* Editable Title */}
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="text-xl md:text-[32px] font-semibold border-none shadow-none px-0 h-auto py-2 mb-4 md:mb-6 focus-visible:ring-0 bg-transparent"
              placeholder="Titel eingeben..."
            />

            {/* Rich Text Editor */}
            <RichTextEditor
              content={editContent}
              onChange={setEditContent}
              placeholder="Inhalt hier eingeben..."
              onFileAttach={(file) => {
                // Trigger file upload for the attached file
                const fakeEvent = {
                  target: { files: [file], value: '' }
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                handleFileUpload(fakeEvent);
              }}
            />

            {/* Attachments Section in Edit Mode */}
            <div className="mt-8 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Anhänge
                </h3>
              </div>

              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((att, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30 group"
                    >
                      <span className="text-xl">{getFileIcon(att.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{att.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(att.size)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 h-8 w-8"
                        onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </article>
        </ScrollArea>
      </div>
    );
  }

  // View mode
  return (
    <div className="flex-1 flex flex-col h-full bg-white dark:bg-background">
      {/* Confluence-style Page Header */}
      <div className="border-b bg-white dark:bg-card shrink-0">
        {/* Breadcrumb Row */}
        <div className="px-6 py-2 flex items-center justify-between border-b border-border/50">
          <nav className="flex items-center gap-1 text-sm">
            <button 
              onClick={onBack}
              className="text-[#0052CC] dark:text-primary hover:underline"
            >
              Wissensbank
            </button>
            {entry.category && (
              <>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span className="text-[#0052CC] dark:text-primary hover:underline cursor-pointer">
                  {entry.category}
                </span>
              </>
            )}
          </nav>

          {/* Actions - Confluence style: icon buttons on the right */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onTogglePin}
              className={entry.is_pinned ? "text-amber-500" : "text-muted-foreground"}
            >
              {entry.is_pinned ? (
                <Star className="h-4 w-4 fill-current" />
              ) : (
                <StarOff className="h-4 w-4" />
              )}
            </Button>

            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleStartEdit}>
                    <Edit className="h-4 w-4 mr-2" />
                    Bearbeiten
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onTogglePin}>
                    {entry.is_pinned ? (
                      <>
                        <StarOff className="h-4 w-4 mr-2" />
                        Von Favoriten entfernen
                      </>
                    ) : (
                      <>
                        <Star className="h-4 w-4 mr-2" />
                        Zu Favoriten hinzufügen
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Page Content */}
      <ScrollArea className="flex-1">
        <article className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
          {/* Page Header */}
          <header className="mb-6 md:mb-8">
            {/* Title - Large, like Confluence */}
            <h1 className="text-xl sm:text-2xl md:text-[36px] font-semibold text-foreground mb-4 md:mb-6 leading-tight break-words">
              {entry.title}
            </h1>
            
            {/* Author & Meta Row - Confluence style */}
            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Avatar className="h-5 w-5 md:h-6 md:w-6">
                  <AvatarImage src={creatorAvatar} />
                  <AvatarFallback className="text-[10px] md:text-xs bg-[#0052CC]/10 text-[#0052CC] dark:bg-primary/10 dark:text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-foreground">
                  {creatorName || 'Unbekannt'}
                </span>
              </div>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">
                {format(new Date(entry.created_at), 'dd. MMMM yyyy', { locale: de })}
              </span>
              <span className="hidden md:inline">•</span>
              <span className="hidden md:flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Bearbeitet {formatDistanceToNow(new Date(entry.updated_at), { 
                  addSuffix: true, 
                  locale: de 
                })}
              </span>
            </div>

            {/* Category Badge */}
            {entry.category && (
              <div className="mt-3 md:mt-4">
                <Badge variant="secondary" className="bg-[#DEEBFF] text-[#0052CC] dark:bg-primary/20 dark:text-primary border-0 text-xs">
                  {entry.category}
                </Badge>
              </div>
            )}
          </header>

          {/* Clickable edit hint for canEdit users */}
          {canEdit && (
            <button
              onClick={handleStartEdit}
              className="w-full text-left mb-4 sm:mb-6 p-2.5 sm:p-3 rounded-lg border border-dashed border-border hover:border-[#0052CC] dark:hover:border-primary hover:bg-[#DEEBFF]/30 dark:hover:bg-primary/10 transition-all flex items-center gap-2"
            >
              <Edit className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-xs sm:text-sm text-muted-foreground">
                Klicke zum Bearbeiten...
              </span>
            </button>
          )}

          {/* Content Body */}
          <div
            className="confluence-content prose prose-sm sm:prose-base md:prose-lg prose-neutral dark:prose-invert max-w-none
              prose-headings:font-semibold prose-headings:text-foreground
              prose-h1:text-lg sm:prose-h1:text-xl md:prose-h1:text-[28px] prose-h1:mt-4 sm:prose-h1:mt-6 md:prose-h1:mt-8 prose-h1:mb-2 sm:prose-h1:mb-3 md:prose-h1:mb-4
              prose-h2:text-base sm:prose-h2:text-lg md:prose-h2:text-[24px] prose-h2:mt-4 sm:prose-h2:mt-6 md:prose-h2:mt-8 prose-h2:mb-2 sm:prose-h2:mb-3 md:prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b
              prose-h3:text-sm sm:prose-h3:text-base md:prose-h3:text-[20px] prose-h3:mt-3 sm:prose-h3:mt-4 md:prose-h3:mt-6 prose-h3:mb-1.5 sm:prose-h3:mb-2 md:prose-h3:mb-3
              prose-p:text-foreground/90 prose-p:leading-relaxed sm:prose-p:leading-6 md:prose-p:leading-7 prose-p:my-2 sm:prose-p:my-3 md:prose-p:my-4
              prose-a:text-[#0052CC] dark:prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-2 sm:prose-ul:my-3 md:prose-ul:my-4 prose-ol:my-2 sm:prose-ol:my-3 md:prose-ol:my-4
              prose-li:text-foreground/90 prose-li:my-0.5 sm:prose-li:my-1
              prose-blockquote:border-l-4 prose-blockquote:border-[#0052CC] dark:prose-blockquote:border-primary prose-blockquote:bg-[#DEEBFF] dark:prose-blockquote:bg-primary/10 prose-blockquote:py-2 prose-blockquote:px-2 sm:prose-blockquote:px-3 md:prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:my-2 sm:prose-blockquote:my-3 md:prose-blockquote:my-4 prose-blockquote:rounded-r
              prose-code:text-[#0052CC] dark:prose-code:text-primary prose-code:bg-muted prose-code:px-1 sm:prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-[11px] sm:prose-code:text-xs md:prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-[#F4F5F7] dark:prose-pre:bg-muted prose-pre:rounded-lg prose-pre:p-2 sm:prose-pre:p-3 md:prose-pre:p-4 prose-pre:overflow-x-auto prose-pre:text-xs sm:prose-pre:text-sm
              prose-img:rounded-lg prose-img:shadow-sm prose-img:max-w-full
              prose-table:border-collapse prose-table:w-full prose-table:text-xs sm:prose-table:text-sm
              prose-th:bg-[#F4F5F7] dark:prose-th:bg-muted prose-th:p-1.5 sm:prose-th:p-2 md:prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:border
              prose-td:p-1.5 sm:prose-td:p-2 md:prose-td:p-3 prose-td:border"
            dangerouslySetInnerHTML={{ __html: entry.content }}
          />

          {/* Attachments Section - Confluence Style */}
          {attachments.length > 0 && (
            <div className="mt-8 sm:mt-10 md:mt-12">
              {/* Confluence-style Attachments Header */}
              <div className="flex items-center gap-2 pb-2 sm:pb-3 border-b border-border/50">
                <Paperclip className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <h3 className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Anhänge ({attachments.length})
                </h3>
              </div>
              
              {/* Confluence-style Attachments Table */}
              <div className="mt-3 sm:mt-4">
                <div className="bg-muted/20 rounded-lg border overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-[1fr,45px,32px] sm:grid-cols-[1fr,50px,40px] md:grid-cols-[1fr,80px,100px] gap-1 md:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-muted/40 border-b text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <span>Name</span>
                    <span className="text-right">Größe</span>
                    <span></span>
                  </div>
                  
                  {/* Table Rows */}
                  {attachments.map((att, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "grid grid-cols-[1fr,45px,32px] sm:grid-cols-[1fr,50px,40px] md:grid-cols-[1fr,80px,100px] gap-1 md:gap-2 px-2 sm:px-4 py-2 sm:py-3 items-center group",
                        "hover:bg-muted/30 transition-colors",
                        idx !== attachments.length - 1 && "border-b border-border/30"
                      )}
                    >
                      {/* File Name with Icon/Thumbnail */}
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {isImageFile(att.type) ? (
                          <div 
                            className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded overflow-hidden bg-muted/50 cursor-pointer hover:ring-2 ring-primary transition-all"
                            onClick={() => setPreviewAttachment(att)}
                          >
                            <img 
                              src={att.url} 
                              alt={att.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : isPdfFile(att.type) ? (
                          <div 
                            className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded overflow-hidden bg-muted/50 cursor-pointer hover:ring-2 ring-primary transition-all flex items-center justify-center"
                            onClick={() => setPreviewAttachment(att)}
                          >
                            <iframe 
                              src={`${att.url}#page=1&view=FitH`}
                              title={att.name}
                              className="w-full h-full pointer-events-none scale-[2] origin-center"
                              style={{ border: 'none' }}
                            />
                          </div>
                        ) : (
                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded bg-muted/50 flex items-center justify-center">
                            <span className="text-sm sm:text-lg">{getFileIcon(att.type)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
                          <span 
                            className={cn(
                              "text-xs sm:text-sm font-medium truncate text-foreground",
                              (isImageFile(att.type) || isPdfFile(att.type)) && "cursor-pointer hover:text-primary"
                            )}
                            onClick={() => {
                              if (isImageFile(att.type) || isPdfFile(att.type)) {
                                setPreviewAttachment(att);
                              }
                            }}
                          >
                            {att.name}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 sm:h-6 sm:w-6 p-0 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingAttachmentIndex(idx);
                              setEditingAttachmentName(att.name);
                            }}
                          >
                            <Pencil className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* File Size */}
                      <span className="text-[10px] sm:text-xs md:text-sm text-muted-foreground text-right">
                        {formatFileSize(att.size)}
                      </span>
                      
                      {/* Actions */}
                      <div className="flex justify-end">
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-6 w-6 sm:h-8 sm:w-auto sm:px-2 text-xs font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors"
                        >
                          <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          <span className="hidden sm:inline sm:ml-1">Download</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Page Footer */}
          <footer className="mt-8 sm:mt-10 md:mt-12 pt-4 sm:pt-6 border-t">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-muted-foreground gap-2">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="break-words">
                  Aktualisiert: {format(new Date(entry.updated_at), 'dd.MM.yyyy, HH:mm', { locale: de })} Uhr
                </span>
              </div>
            </div>
          </footer>
        </article>
      </ScrollArea>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Seite löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchtest du "{entry.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onDelete();
                setShowDeleteDialog(false);
              }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Attachment Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={(open) => !open && setPreviewAttachment(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <VisuallyHidden>
            <DialogTitle>{previewAttachment?.name || 'Dateivorschau'}</DialogTitle>
          </VisuallyHidden>
          
          {previewAttachment && (
            <div className="flex flex-col h-full">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg">{getFileIcon(previewAttachment.type)}</span>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{previewAttachment.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(previewAttachment.size)}
                      {attachments.length > 1 && ` • ${attachments.findIndex(a => a.url === previewAttachment.url) + 1} von ${attachments.length}`}
                    </p>
                  </div>
                </div>
                <a
                  href={previewAttachment.url}
                  download={previewAttachment.name}
                  className="inline-flex items-center justify-center p-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded transition-colors"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </a>
              </div>
              
              {/* Preview Content with Navigation Arrows */}
              <div className="flex-1 overflow-auto bg-muted/10 flex items-center justify-center p-4 relative">
                {/* Previous Arrow */}
                {attachments.length > 1 && (
                  <button
                    onClick={() => {
                      const currentIndex = attachments.findIndex(a => a.url === previewAttachment.url);
                      const prevIndex = currentIndex > 0 ? currentIndex - 1 : attachments.length - 1;
                      setPreviewAttachment(attachments[prevIndex]);
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-background/80 hover:bg-background rounded-full shadow-lg transition-colors z-10"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                )}

                {isImageFile(previewAttachment.type) ? (
                  <img 
                    src={previewAttachment.url} 
                    alt={previewAttachment.name}
                    className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                  />
                ) : isPdfFile(previewAttachment.type) ? (
                  <iframe
                    src={previewAttachment.url}
                    title={previewAttachment.name}
                    className="w-full h-[70vh] rounded border"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Vorschau nicht verfügbar</p>
                  </div>
                )}

                {/* Next Arrow */}
                {attachments.length > 1 && (
                  <button
                    onClick={() => {
                      const currentIndex = attachments.findIndex(a => a.url === previewAttachment.url);
                      const nextIndex = currentIndex < attachments.length - 1 ? currentIndex + 1 : 0;
                      setPreviewAttachment(attachments[nextIndex]);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-background/80 hover:bg-background rounded-full shadow-lg transition-colors z-10"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename Attachment Dialog */}
      <Dialog open={editingAttachmentIndex !== null} onOpenChange={(open) => !open && setEditingAttachmentIndex(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Datei umbenennen</DialogTitle>
          <div className="space-y-4 py-4">
            <Input
              value={editingAttachmentName}
              onChange={(e) => setEditingAttachmentName(e.target.value)}
              placeholder="Dateiname"
              className="text-base"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editingAttachmentIndex !== null) {
                  const updatedAttachments = [...attachments];
                  updatedAttachments[editingAttachmentIndex] = { ...updatedAttachments[editingAttachmentIndex], name: editingAttachmentName };
                  setAttachments(updatedAttachments);
                  onSave({ title: entry.title, content: entry.content, attachments: updatedAttachments }, entry.id);
                  setEditingAttachmentIndex(null);
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditingAttachmentIndex(null)}
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => {
                  if (editingAttachmentIndex !== null) {
                    const updatedAttachments = [...attachments];
                    updatedAttachments[editingAttachmentIndex] = { ...updatedAttachments[editingAttachmentIndex], name: editingAttachmentName };
                    setAttachments(updatedAttachments);
                    onSave({ title: entry.title, content: entry.content, attachments: updatedAttachments }, entry.id);
                    setEditingAttachmentIndex(null);
                  }
                }}
              >
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfluencePageView;
